import React, { useCallback, useEffect, useRef, useState } from 'react';
import { WaIcon, waHref } from '../../components/WhatsApp.jsx';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, getToken } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import { useI18n } from '../../i18n/index.jsx';
import { useCategories } from '../../data.js';
import { Alert } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import AddressAutocomplete from '../../components/AddressAutocomplete.jsx';
import { countryOptions } from '../../countries.js';
import { Modal, StatusBadge, CUSTOMER_STATUSES, SOURCES, COUNTRIES, countryName, fmtDate, today } from './ws.jsx';
import TutorialSlot from '../../tutorials/TutorialSlot.jsx';

// Customers (CRM-light, migration 030) + "My customers" (migration 035):
// two sources in one list — registered member companies (followed by
// mine), and private contacts this company added itself (never visible to
// other companies, never on the public site). A rep sees the ones
// assigned to them; "view all" users filter by user, assign in bulk,
// import/export, send small campaigns.
// Search, filters, sort, saved filters, columns, tags and types (migration
// 039): the whole filter lives in the page address (bookmark it), and the
// export / bulk actions use exactly what is shown.
const COLUMNS = [
  { key: 'type', sort: 'type' }, { key: 'source' }, { key: 'country', sort: 'country' }, { key: 'tags' }, { key: 'email' }, { key: 'phone' },
  { key: 'sector' }, { key: 'status', sort: 'status' }, { key: 'lastContact', sort: 'lastContact' }, { key: 'followup', sort: 'followup' },
  { key: 'replies', sort: 'replies' }, { key: 'created', sort: 'created' }, { key: 'assignee', sort: 'assignee', all: true },
];
const DEFAULT_COLS = ['type', 'source', 'country', 'tags', 'status', 'lastContact', 'followup', 'assignee'];
const COLS_KEY = 'pme_customer_columns';
const FILTER_KEYS = ['q', 'type', 'tags', 'country', 'sector', 'status', 'assignee', 'due', 'hasEmail', 'replies', 'from', 'to'];
const loadCols = () => { try { const v = JSON.parse(localStorage.getItem(COLS_KEY) || 'null'); return Array.isArray(v) ? v : DEFAULT_COLS; } catch { return DEFAULT_COLS; } };

// Customer types (an editable list per company, migration 039).
let typesCache = null;
export function useContactTypes() {
  const [types, setTypes] = useState(typesCache);
  useEffect(() => {
    if (typesCache) return;
    api('/workspace/customers/types').then((r) => { typesCache = r.types; setTypes(r.types); }).catch(() => setTypes([]));
  }, []);
  return types || [];
}
export const typeLabel = (types, code, lang) => { const x = types.find((y) => y.code === code); return x ? (lang === 'en' ? x.en : x.fr) : code || ''; };
const TagChip = ({ name, color }) => <span className="ws-tag" style={{ '--tag': color || '#64748b' }}>{name}</span>;

// A small "▾" pop-over (columns, tags filter).
function Pop({ label, children, className = '' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  return (
    <span className={`ws-pop ${className}`} ref={ref}>
      <button type="button" className="btn btn-ghost btn-sm" aria-expanded={open} onClick={() => setOpen((o) => !o)}>{label} ▾</button>
      {open && <span className="ws-pop-list">{children}</span>}
    </span>
  );
}

export default function Customers({ admin = false, base = '/account/workspace/customers' }) {
  const { t, lang, errorText } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const get = (k) => params.get(k) || '';
  const source = params.get('source') || 'all';
  const setP = useCallback((changes) => setParams((p) => {
    const n = new URLSearchParams(p);
    for (const [k, v] of Object.entries(changes)) { if (v === '' || v == null || v === false) n.delete(k); else n.set(k, v === true ? '1' : String(v)); }
    return n;
  }, { replace: true }), [setParams]);
  const query = params.toString();
  const [data, setData] = useState(null);
  const [tags, setTags] = useState([]);
  const [saved, setSaved] = useState([]);
  const [qText, setQText] = useState(get('q'));
  const [sectorText, setSectorText] = useState(get('sector'));
  const [cols, setCols] = useState(loadCols);
  const [showFilters, setShowFilters] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [to, setTo] = useState('');
  const [bulkTag, setBulkTag] = useState('');
  const [bulkType, setBulkType] = useState('');
  const [adding, setAdding] = useState(false);
  const [newContact, setNewContact] = useState(false);
  const [importing, setImporting] = useState(false);
  const [bulkEmailing, setBulkEmailing] = useState(false);
  const [managing, setManaging] = useState(null); // 'tags' | 'types'
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  // Search / sector boxes: typed here, written to the address after a pause.
  useEffect(() => { const tm = setTimeout(() => { if (qText !== (params.get('q') || '')) setP({ q: qText }); }, 300); return () => clearTimeout(tm); }, [qText]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const tm = setTimeout(() => { if (sectorText !== (params.get('sector') || '')) setP({ sector: sectorText }); }, 400); return () => clearTimeout(tm); }, [sectorText]); // eslint-disable-line react-hooks/exhaustive-deps
  // Back / a saved filter / a bookmark: the boxes follow the address.
  useEffect(() => { setQText(params.get('q') || ''); setSectorText(params.get('sector') || ''); }, [params]);

  const load = useCallback(() => api(`/workspace/customers?${query}`).then((d) => { setData(d); setError(''); }).catch((e) => setError(errorText(e))), [query, errorText]);
  useEffect(() => { load(); }, [load]);
  const loadTags = useCallback(() => api('/workspace/customers/tags').then((r) => setTags(r.tags)).catch(() => {}), []);
  useEffect(() => { loadTags(); api('/workspace/customers/filters').then((r) => setSaved(r.filters)).catch(() => {}); }, [loadTags]);
  useEffect(() => { setSel(new Set()); }, [query]);

  const list = data?.customers || [];
  const types = data?.types || [];
  const tagColor = (n) => tags.find((x) => x.name === n)?.color;
  const allOn = list.length > 0 && list.every((c) => sel.has(c.id));
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const visible = COLUMNS.filter((c) => cols.includes(c.key) && (!c.all || data?.seesAll));
  const setColumns = (next) => { setCols(next); try { localStorage.setItem(COLS_KEY, JSON.stringify(next)); } catch { /* private mode */ } };
  const active = FILTER_KEYS.filter((k) => params.get(k)).length;
  const sortBy = (k) => setP(get('sort') === k ? { dir: get('dir') === 'desc' ? '' : 'desc' } : { sort: k, dir: '' });
  const tagsOn = get('tags') ? get('tags').split(',') : [];

  async function bulk(action, extra) {
    try { const r = await api('/workspace/customers/bulk', { method: 'POST', body: { ids: [...sel], action, ...extra } }); setMsg(t('ws.cust.updatedN', { n: r.updated })); load(); loadTags(); } catch (e) { setError(errorText(e)); }
  }
  async function assign() {
    try {
      const r = await api('/workspace/customers/assign', { method: 'POST', body: { ids: [...sel], assignedTo: to ? Number(to) : null } });
      setMsg(t('ws.cust.assignedN', { n: r.updated })); setSel(new Set()); load();
    } catch (e) { setError(errorText(e)); }
  }
  async function doExport(format, ids) {
    try {
      const qs = new URLSearchParams(params);
      qs.set('format', format);
      if (ids?.length) qs.set('ids', ids.join(','));
      const r = await fetch(`/api/workspace/customers/export?${qs}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `customers.${format}`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { setError(errorText({ code: 'server_error' })); }
  }
  async function saveFilter() {
    const name = window.prompt(t('ws.cust.saveFilterPrompt'));
    if (!name?.trim()) return;
    const qs = new URLSearchParams(params); qs.delete('sort'); qs.delete('dir');
    try { const f = await api('/workspace/customers/filters', { method: 'POST', body: { name: name.trim(), query: qs.toString() } }); setSaved((s) => [...s, f].sort((a, b) => a.name.localeCompare(b.name))); setMsg(t('ws.cust.filterSaved')); } catch (e) { setError(errorText(e)); }
  }
  const currentSaved = saved.find((f) => f.query === (() => { const qs = new URLSearchParams(params); qs.delete('sort'); qs.delete('dir'); return qs.toString(); })());
  async function deleteSaved(f) {
    if (!window.confirm(t('ws.cust.deleteFilterConfirm', { name: f.name }))) return;
    await api(`/workspace/customers/filters/${f.id}`, { method: 'DELETE' }).catch(() => {});
    setSaved((s) => s.filter((x) => x.id !== f.id));
  }
  const cell = (c, k) => {
    switch (k) {
      case 'type': return c.contactType ? typeLabel(types, c.contactType, lang) : '';
      case 'source': return <span className={`ws-badge ${c.isMember ? 'ws-crm-won' : 'ws-crm-prospect'}`}>{t(c.isMember ? 'ws.cust.sourceMember' : 'ws.cust.sourcePrivate')}</span>;
      case 'country': return `${c.country ? countryName(c.country, lang) : ''}${c.city ? ` · ${c.city}` : ''}`;
      case 'tags': return <span className="ws-tags">{c.tags.map((x) => <TagChip key={x} name={x} color={tagColor(x)} />)}</span>;
      case 'email': return c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : '';
      case 'phone': return c.phone || '';
      case 'sector': return c.sector || '';
      case 'status': return <StatusBadge kind="crm" value={c.status} />;
      case 'lastContact': return fmtDate(c.lastContactAt, lang) || '—';
      case 'followup': return <span className={c.nextFollowup && c.nextFollowup <= today() ? 'field-error' : ''}>{fmtDate(c.nextFollowup, lang) || '—'}</span>;
      case 'replies': return c.replies ? <span className="ws-badge ws-crm-won">✉ {c.replies}</span> : '';
      case 'created': return fmtDate(c.createdAt, lang) || '';
      case 'assignee': return c.assignedTo?.name || <span className="muted">{t('ws.cust.unassigned')}</span>;
      default: return '';
    }
  };

  return (
    <div className="ws-page ws-customers">
      <div className="acc-head"><TutorialSlot place={admin ? [] : source === 'members' ? 'ws.customers.members' : source === 'mine' ? 'ws.customers.mine' : ['ws.customers.mine', 'ws.customers.members']} />
        <div><h1>{admin ? t('ws.cust.pmeTitle') : t('ws.cust.title')}</h1><p className="muted">{data?.seesAll ? t('ws.cust.leadAll') : t('ws.cust.leadMine')}</p></div>
        <div className="acc-head-actions ws-row">
          <button type="button" className="btn btn-primary" onClick={() => setNewContact(true)}>{t('ws.cust.newPrivate')}</button>
          <button type="button" className="btn btn-ghost" onClick={() => setAdding(true)}>+ {t('ws.cust.add')}</button>
        </div>
      </div>
      {!admin && (
        <div className="ws-tabs" role="tablist" aria-label={t('ws.cust.title')}>
          {['all', 'mine', 'members'].map((s) => (
            <button key={s} type="button" role="tab" aria-selected={source === s} className={`ws-tab${source === s ? ' is-active' : ''}`} onClick={() => setP({ source: s === 'all' ? '' : s })}>
              {t(`ws.cust.tab.${s}`)}{data?.counts ? ` (${s === 'all' ? data.counts.all : s === 'mine' ? data.counts.mine : data.counts.members})` : ''}
            </button>
          ))}
        </div>
      )}
      <Alert>{error}</Alert>
      {msg && <p className="callout callout-success" role="status">{msg}</p>}

      <div className="cust-search-row">
        <label className="acc-search"><Icon name="search" size={18} /><input type="search" value={qText} onChange={(e) => setQText(e.target.value)} placeholder={t('ws.cust.searchAll')} aria-label={t('ws.cust.searchAll')} /></label>
        <button type="button" className={`btn btn-ghost btn-sm cust-filters-btn${showFilters ? ' is-on' : ''}`} aria-expanded={showFilters} onClick={() => setShowFilters((x) => !x)}>⚲ {t('ws.cust.filters')}{active ? ` (${active})` : ''}</button>
        <span className="cust-saved">
          <select value={currentSaved ? currentSaved.id : ''} onChange={(e) => { const f = saved.find((x) => String(x.id) === e.target.value); if (f) setParams(new URLSearchParams(f.query), { replace: false }); }} aria-label={t('ws.cust.savedFilters')}>
            <option value="">{t('ws.cust.savedFilters')}{saved.length ? ` (${saved.length})` : ''}</option>
            {saved.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          {currentSaved ? <button type="button" className="btn btn-link btn-sm" onClick={() => deleteSaved(currentSaved)}>{t('ws.cust.deleteFilter')}</button>
            : <button type="button" className="btn btn-ghost btn-sm" onClick={saveFilter} disabled={!active && source === 'all'}>💾 {t('ws.cust.saveFilter')}</button>}
        </span>
      </div>

      <div className={`cust-filters${showFilters ? ' is-open' : ''}`}>
        <label className="field"><span className="field-label">{t('ws.priv.type')}</span>
          <select value={get('type')} onChange={(e) => setP({ type: e.target.value })}><option value="">{t('ws.cust.any')}</option>{types.map((x) => <option key={x.code} value={x.code}>{lang === 'en' ? x.en : x.fr}</option>)}</select></label>
        <div className="field"><span className="field-label">{t('ws.cp.tags')}</span>
          <Pop label={tagsOn.length ? tagsOn.join(', ') : t('ws.cust.any')} className="cust-tags-pop">
            {tags.length === 0 && <span className="muted small">{t('ws.cust.noTags')}</span>}
            {tags.map((x) => (
              <label key={x.name} className="field-checkbox"><input type="checkbox" checked={tagsOn.includes(x.name)} onChange={(e) => setP({ tags: (e.target.checked ? [...tagsOn, x.name] : tagsOn.filter((y) => y !== x.name)).join(',') })} /><TagChip name={x.name} color={x.color} /><span className="muted small">{x.count}</span></label>
            ))}
          </Pop></div>
        <label className="field"><span className="field-label">{t('ws.cust.country')}</span>
          <select value={get('country')} onChange={(e) => setP({ country: e.target.value })}><option value="">{t('ws.cust.any')}</option>
            {(data?.countries || []).map((c) => ({ c, n: countryName(c, lang) })).sort((a, b) => a.n.localeCompare(b.n)).map(({ c, n }) => <option key={c} value={c}>{n}</option>)}</select></label>
        <label className="field"><span className="field-label">{t('ws.cust.sector')}</span><input value={sectorText} onChange={(e) => setSectorText(e.target.value)} placeholder={t('ws.priv.sectorPh')} /></label>
        <label className="field"><span className="field-label">{t('ws.cust.status')}</span>
          <select value={get('status')} onChange={(e) => setP({ status: e.target.value })}><option value="">{t('ws.cust.allStatuses')}</option>{CUSTOMER_STATUSES.map((s) => <option key={s} value={s}>{t(`ws.crm.${s}`)}</option>)}</select></label>
        {data?.seesAll && (
          <label className="field"><span className="field-label">{t('ws.cust.assignee')}</span>
            <select value={get('assignee')} onChange={(e) => setP({ assignee: e.target.value })}>
              <option value="">{t('ws.cust.everyone')}</option><option value="none">{t('ws.cust.unassigned')}</option>
              {data.assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select></label>
        )}
        <label className="field"><span className="field-label">{t('ws.cust.addedFrom')}</span><input type="date" value={get('from')} onChange={(e) => setP({ from: e.target.value })} /></label>
        <label className="field"><span className="field-label">{t('ws.cust.addedTo')}</span><input type="date" value={get('to')} onChange={(e) => setP({ to: e.target.value })} /></label>
        <div className="cust-checks">
          <label className="field-checkbox"><input type="checkbox" checked={get('due') === '1'} onChange={(e) => setP({ due: e.target.checked })} /><span>{t('ws.cust.dueOnly')}</span></label>
          <label className="field-checkbox"><input type="checkbox" checked={get('hasEmail') === '1'} onChange={(e) => setP({ hasEmail: e.target.checked })} /><span>{t('ws.cust.hasEmail')}</span></label>
          <label className="field-checkbox"><input type="checkbox" checked={get('replies') === '1'} onChange={(e) => setP({ replies: e.target.checked })} /><span>{t('ws.cust.hasReplies')}</span></label>
        </div>
        {active > 0 && <button type="button" className="btn btn-link btn-sm" onClick={() => setP(Object.fromEntries(FILTER_KEYS.map((k) => [k, ''])))}>{t('ws.cust.clearFilters')}</button>}
      </div>

      <div className="acc-toolbar cust-tools">
        <span className="muted small">{data ? t('ws.cust.shownN', { n: list.length, total: data.total }) : ''}</span>
        <span className="ws-grow" />
        <Pop label={t('ws.cust.columns')}>
          {COLUMNS.filter((c) => !c.all || data?.seesAll).map((c) => (
            <label key={c.key} className="field-checkbox"><input type="checkbox" checked={cols.includes(c.key)} onChange={(e) => setColumns(e.target.checked ? [...cols, c.key] : cols.filter((x) => x !== c.key))} /><span>{t(`ws.cust.col.${c.key}`)}</span></label>
          ))}
          <button type="button" className="btn btn-link btn-sm" onClick={() => setColumns(DEFAULT_COLS)}>{t('ws.cust.colsReset')}</button>
        </Pop>
        {data?.canManageLists && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setManaging('tags')}>🏷 {t('ws.cust.manageTags')}</button>}
        {data?.canManageLists && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setManaging('types')}>{t('ws.cust.manageTypes')}</button>}
        {data?.canImport && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setImporting(true)}>⬆ {t('ws.import.btn')}</button>}
        {data?.canExport && (
          <span className="ws-export-group" title={t('ws.cust.exportHint')}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => doExport('csv')}>⬇ CSV</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => doExport('xlsx')}>⬇ Excel</button>
          </span>
        )}
      </div>

      {sel.size > 0 && (
        <div className="acc-bulk ws-row cust-bulk">
          <strong>{t('ws.cust.selected', { n: sel.size })}</strong>
          <span className="cust-bulk-group">
            <input list="cust-tag-list" value={bulkTag} onChange={(e) => setBulkTag(e.target.value)} placeholder={t('ws.cust.tagPh')} aria-label={t('ws.cp.tags')} />
            <datalist id="cust-tag-list">{tags.map((x) => <option key={x.name} value={x.name} />)}</datalist>
            <button type="button" className="btn btn-sm" disabled={!bulkTag.trim()} onClick={() => bulk('tagAdd', { tag: bulkTag.trim() })}>+ {t('ws.cust.tagAdd')}</button>
            <button type="button" className="btn btn-sm" disabled={!bulkTag.trim()} onClick={() => bulk('tagRemove', { tag: bulkTag.trim() })}>− {t('ws.cust.tagRemove')}</button>
          </span>
          <span className="cust-bulk-group">
            <select value={bulkType} onChange={(e) => setBulkType(e.target.value)} aria-label={t('ws.priv.type')}><option value="">{t('ws.cust.noType')}</option>{types.map((x) => <option key={x.code} value={x.code}>{lang === 'en' ? x.en : x.fr}</option>)}</select>
            <button type="button" className="btn btn-sm" onClick={() => bulk('type', { type: bulkType || null })}>{t('ws.cust.changeType')}</button>
          </span>
          {data?.canAssign && (
            <span className="cust-bulk-group">
              <select value={to} onChange={(e) => setTo(e.target.value)} aria-label={t('ws.cust.assignTo')}>
                <option value="">{t('ws.cust.unassigned')}</option>{data.assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <button type="button" className="btn btn-primary btn-sm" onClick={assign}>{t('ws.cust.assign')}</button>
            </span>
          )}
          {data?.canBulkSend && <button type="button" className="btn btn-sm" onClick={() => setBulkEmailing(true)}>✉️ {t('ws.bulk.btn')}</button>}
          <button type="button" className="btn btn-sm" disabled={sel.size !== 1} title={sel.size !== 1 ? t('ws.cust.docOneHint') : undefined} onClick={() => navigate(`${base.replace(/\/customers$/, '')}/documents/new?crmId=${[...sel][0]}`)}>📄 {t('ws.cust.createDoc')}</button>
          {data?.canExport && <button type="button" className="btn btn-sm" onClick={() => doExport('xlsx', [...sel])}>⬇ {t('ws.cust.exportSelected')}</button>}
        </div>
      )}
      {!data ? <p className="muted">{t('common.loading')}</p> : (
        <div className="ws-table-wrap">
          <table className="ws-table ws-table-cards cust-table">
            <thead><tr>
              <th><input type="checkbox" checked={allOn} onChange={() => setSel(allOn ? new Set() : new Set(list.map((c) => c.id)))} aria-label={t('ws.cust.selectAll')} /></th>
              <th aria-sort={get('sort') === 'company' ? (get('dir') === 'desc' ? 'descending' : 'ascending') : undefined}><button type="button" className="th-sort" onClick={() => sortBy('company')}>{t('ws.cust.company')}{get('sort') === 'company' ? (get('dir') === 'desc' ? ' ↓' : ' ↑') : ''}</button></th>
              {visible.map((c) => (
                <th key={c.key} aria-sort={c.sort && get('sort') === c.sort ? (get('dir') === 'desc' ? 'descending' : 'ascending') : undefined}>
                  {c.sort ? <button type="button" className="th-sort" onClick={() => sortBy(c.sort)}>{t(`ws.cust.col.${c.key}`)}{get('sort') === c.sort ? (get('dir') === 'desc' ? ' ↓' : ' ↑') : ''}</button> : t(`ws.cust.col.${c.key}`)}
                </th>
              ))}
            </tr></thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} className={sel.has(c.id) ? 'is-selected' : ''}>
                  <td><input type="checkbox" checked={sel.has(c.id)} onChange={() => toggle(c.id)} aria-label={c.company || c.contact} /></td>
                  <td className="ws-td-main"><Link to={`${base}/${c.id}`}>{c.company || c.contact}</Link>{c.company && c.contact && c.contact !== c.company && <span className="muted small"> · {c.contact}</span>}{c.unsubscribed && <span className="ws-badge ws-crm-lost small"> {t('ws.bulk.unsubscribed')}</span>}
                    {/* WhatsApp (migration 043): opens the chat with a greeting; logged on the timeline. */}
                    {c.whatsapp && <a className="wa-mini" style={{ marginLeft: 8, verticalAlign: 'middle', width: 26, height: 26 }} href={waHref(c.whatsapp, t('wa.customerMessage', { name: c.contact || c.company || '' }))} target="_blank" rel="noreferrer noopener"
                      title={t('wa.openChat')} aria-label={`${t('wa.openChat')} — ${c.company || c.contact}`} onClick={() => { api(`/workspace/whatsapp/customers/${c.id}/opened`, { method: 'POST' }).catch(() => {}); }}><WaIcon size={15} /></a>}</td>
                  {visible.map((col) => <td key={col.key} data-label={t(`ws.cust.col.${col.key}`)} className={['lastContact', 'followup', 'created', 'assignee', 'phone'].includes(col.key) ? 'small' : ''}>{cell(c, col.key)}</td>)}
                </tr>
              ))}
              {!list.length && <tr><td colSpan={visible.length + 2} className="muted">{active || source !== 'all' ? t('ws.cust.noMatch') : t('ws.cust.empty')}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {adding && <AddCustomers pme={!!user.team?.pme} assignees={data?.assignees || []} canAssign={!!data?.canAssign} onClose={() => setAdding(false)} onDone={(n) => { setAdding(false); setMsg(t('ws.cust.addedN', { n })); load(); }} />}
      {newContact && <NewContactModal assignees={data?.assignees || []} canAssign={!!data?.canAssign} onClose={() => setNewContact(false)} onDone={() => { setNewContact(false); setMsg(t('ws.priv.added')); load(); loadTags(); }} />}
      {importing && <ImportWizard assignees={data?.assignees || []} canAssign={!!data?.canAssign} onClose={() => setImporting(false)} onDone={(r) => { setImporting(false); setMsg(t('ws.import.doneN', { n: r.imported, linked: r.linked })); load(); }} />}
      {bulkEmailing && <BulkEmailModal ids={[...sel]} onClose={() => setBulkEmailing(false)} onDone={(r) => { setBulkEmailing(false); setSel(new Set()); setMsg(t('ws.bulk.startedN', { n: r.pending })); }} />}
      {managing === 'tags' && <TagManager tags={tags} onChanged={(next) => { setTags(next); load(); }} onClose={() => setManaging(null)} />}
      {managing === 'types' && <TypeManager types={types} onSaved={(next) => { typesCache = next; setManaging(null); load(); }} onClose={() => setManaging(null)} />}
    </div>
  );
}

// Tags: colour, rename (every customer follows), merge, remove, create.
function TagManager({ tags, onChanged, onClose }) {
  const { t, errorText } = useI18n();
  const [pick, setPick] = useState(new Set());
  const [into, setInto] = useState('');
  const [nw, setNw] = useState({ name: '', color: '#2563eb' });
  const [error, setError] = useState('');
  const run = async (p) => { setError(''); try { const r = await p; onChanged(r.tags); return r; } catch (e) { setError(errorText(e)); return null; } };
  const rename = (x, v) => { const n = v.trim(); if (n && n !== x.name) run(api('/workspace/customers/tags', { method: 'PATCH', body: { name: x.name, newName: n } })); };
  return (
    <Modal title={t('ws.cust.manageTags')} onClose={onClose}>
      <ul className="cust-tag-admin">
        {tags.map((x) => (
          <li key={x.name}>
            <input type="checkbox" checked={pick.has(x.name)} onChange={(e) => setPick((s) => { const n = new Set(s); if (e.target.checked) n.add(x.name); else n.delete(x.name); return n; })} aria-label={x.name} />
            <input type="color" value={x.color} onChange={(e) => run(api('/workspace/customers/tags', { method: 'PATCH', body: { name: x.name, color: e.target.value } }))} aria-label={t('ws.cust.tagColor')} />
            <input defaultValue={x.name} maxLength={40} onBlur={(e) => rename(x, e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} aria-label={t('ws.cust.tagName')} />
            <span className="muted small">{t('ws.cust.tagCount', { n: x.count })}</span>
          </li>
        ))}
        {!tags.length && <li className="muted">{t('ws.cust.noTags')}</li>}
      </ul>
      {pick.size > 0 && (
        <div className="ws-row cust-merge">
          <input value={into} onChange={(e) => setInto(e.target.value)} placeholder={t('ws.cust.mergeInto')} list="cust-tag-merge" />
          <datalist id="cust-tag-merge">{tags.map((x) => <option key={x.name} value={x.name} />)}</datalist>
          <button type="button" className="btn btn-primary btn-sm" disabled={!into.trim()} onClick={async () => { if (await run(api('/workspace/customers/tags/merge', { method: 'POST', body: { from: [...pick], into: into.trim() } }))) { setPick(new Set()); setInto(''); } }}>{t('ws.cust.merge', { n: pick.size })}</button>
          <button type="button" className="btn btn-link btn-sm text-danger" onClick={async () => { if (!window.confirm(t('ws.cust.removeTagsConfirm', { n: pick.size }))) return; if (await run(api('/workspace/customers/tags/merge', { method: 'POST', body: { from: [...pick], into: '' } }))) setPick(new Set()); }}>{t('ws.cust.removeTags')}</button>
        </div>
      )}
      <div className="ws-row cust-tag-new">
        <input type="color" value={nw.color} onChange={(e) => setNw({ ...nw, color: e.target.value })} aria-label={t('ws.cust.tagColor')} />
        <input value={nw.name} onChange={(e) => setNw({ ...nw, name: e.target.value })} maxLength={40} placeholder={t('ws.cust.newTag')} />
        <button type="button" className="btn btn-ghost btn-sm" disabled={!nw.name.trim()} onClick={async () => { if (await run(api('/workspace/customers/tags', { method: 'POST', body: nw }))) setNw({ name: '', color: nw.color }); }}>+ {t('ws.cust.createTag')}</button>
      </div>
      <Alert>{error}</Alert>
      <div className="ws-actions"><button type="button" className="btn btn-primary" onClick={onClose}>{t('common.close')}</button></div>
    </Modal>
  );
}

// Customer types: the company's own list (FR / EN names, order).
function TypeManager({ types, onSaved, onClose }) {
  const { t, errorText } = useI18n();
  const [list, setList] = useState(() => types.map((x) => ({ ...x })));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (i, c) => setList((l) => l.map((x, j) => (j === i ? { ...x, ...c } : x)));
  const move = (i, d) => setList((l) => { const n = [...l]; const [x] = n.splice(i, 1); n.splice(i + d, 0, x); return n; });
  async function save() {
    setBusy(true); setError('');
    try { const r = await api('/workspace/customers/types', { method: 'PUT', body: { types: list.filter((x) => x.fr.trim()) } }); onSaved(r.types); } catch (e) { setError(errorText(e)); setBusy(false); }
  }
  return (
    <Modal title={t('ws.cust.manageTypes')} onClose={onClose}>
      <p className="ws-hint">{t('ws.cust.typesHint')}</p>
      <ul className="cust-type-admin">
        {list.map((x, i) => (
          <li key={x.code || `n${i}`}>
            <input value={x.fr} maxLength={80} onChange={(e) => set(i, { fr: e.target.value })} placeholder="Français" aria-label="Français" />
            <input value={x.en} maxLength={80} onChange={(e) => set(i, { en: e.target.value })} placeholder="English" aria-label="English" />
            <button type="button" className="btn btn-ghost btn-sm" disabled={i === 0} onClick={() => move(i, -1)} aria-label="↑">↑</button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={i === list.length - 1} onClick={() => move(i, 1)} aria-label="↓">↓</button>
            <button type="button" className="btn btn-link btn-sm text-danger" onClick={() => setList((l) => l.filter((_, j) => j !== i))} aria-label={t('common.delete')}>✕</button>
          </li>
        ))}
      </ul>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setList((l) => [...l, { code: '', fr: '', en: '' }])}>+ {t('ws.cust.addType')}</button>
      <Alert>{error}</Alert>
      <div className="ws-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={save}>{busy ? t('common.saving') : t('common.save')}</button>
      </div>
    </Modal>
  );
}

// Add customers: search, or (PME) by country / province / sector, then assign.
function AddCustomers({ pme, assignees, canAssign, onClose, onDone }) {
  const { t, lang, errorText } = useI18n();
  const { user } = useAuth();
  const categories = useCategories();
  const sectors = categories.filter((c) => !c.parentId);
  const [q, setQ] = useState('');
  const [country, setCountry] = useState('');
  const [province, setProvince] = useState('');
  const [sector, setSector] = useState('');
  const [list, setList] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [to, setTo] = useState(String(user.person?.id || ''));
  const [error, setError] = useState('');
  useEffect(() => {
    const qs = new URLSearchParams({ ...(q ? { q } : {}), ...(country ? { country } : {}), ...(province ? { province } : {}), ...(sector ? { sector } : {}), all: country || province || sector ? '1' : '0' });
    const tm = setTimeout(() => api(`/workspace/candidates?${qs}`).then(setList).catch((e) => setError(errorText(e))), 250);
    return () => clearTimeout(tm);
  }, [q, country, province, sector, errorText]);
  async function add() {
    try { const r = await api('/workspace/customers', { method: 'POST', body: { customerIds: [...sel], ...(canAssign ? { assignedTo: to ? Number(to) : null } : {}) } }); onDone(r.added); } catch (e) { setError(errorText(e)); }
  }
  const allOn = !!list?.length && list.every((c) => sel.has(c.id));
  return (
    <Modal title={t('ws.cust.addTitle')} onClose={onClose} wide>
      <p className="ws-hint">{pme ? t('ws.cust.addLeadPme') : t('ws.cust.addLead')}</p>
      <div className="acc-toolbar">
        <label className="acc-search"><Icon name="search" size={18} /><input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('ws.cust.search')} aria-label={t('ws.cust.search')} /></label>
        {pme && (
          <>
            <select value={country} onChange={(e) => setCountry(e.target.value)} aria-label={t('ws.cust.country')}><option value="">{t('ws.cust.anyCountry')}</option>{COUNTRIES.map((c) => <option key={c} value={c}>{countryName(c, lang)}</option>)}</select>
            <input value={province} onChange={(e) => setProvince(e.target.value.toUpperCase())} placeholder={t('ws.cust.province')} aria-label={t('ws.cust.province')} maxLength={3} style={{ width: 110 }} />
            <select value={sector} onChange={(e) => setSector(e.target.value)} aria-label={t('ws.cust.sector')}><option value="">{t('ws.cust.anySector')}</option>{sectors.map((s) => <option key={s.id} value={s.id}>{lang === 'en' ? s.nameEn : s.nameFr}</option>)}</select>
          </>
        )}
      </div>
      {!list ? <p className="muted">{t('common.loading')}</p> : (
        <div className="ws-table-wrap" style={{ maxHeight: 340, overflow: 'auto' }}>
          <table className="ws-table">
            <thead><tr><th><input type="checkbox" checked={allOn} onChange={() => setSel(allOn ? new Set() : new Set(list.map((c) => c.id)))} aria-label={t('ws.cust.selectAll')} /></th><th>{t('ws.cust.company')}</th><th>{t('ws.cust.country')}</th></tr></thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id}>
                  <td><input type="checkbox" checked={sel.has(c.id)} onChange={() => setSel((s) => { const n = new Set(s); if (n.has(c.id)) n.delete(c.id); else n.add(c.id); return n; })} aria-label={c.company} /></td>
                  <td>{c.company}</td><td className="small">{[c.city, c.province, c.country && countryName(c.country, lang)].filter(Boolean).join(', ')}</td>
                </tr>
              ))}
              {!list.length && <tr><td colSpan={3} className="muted">{pme ? t('ws.cust.noCandidates') : t('ws.cust.noCandidatesCompany')}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {canAssign && (
        <label className="field"><span className="field-label">{t('ws.cust.assignTo')}</span>
          <select value={to} onChange={(e) => setTo(e.target.value)}><option value="">{t('ws.cust.unassigned')}</option>{assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></label>
      )}
      <Alert>{error}</Alert>
      <div className="ws-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
        <button type="button" className="btn btn-primary" disabled={!sel.size} onClick={add}>{t('ws.cust.addN', { n: sel.size })}</button>
      </div>
    </Modal>
  );
}

// "+ Nouveau client": a private contact this company adds itself — never a
// registered member, never visible to other companies or the public site.
// Also doubles as the "edit" form (editId set): same fields, PATCHed in place.
export function NewContactModal({ assignees, canAssign, initial = null, editId = null, onClose, onDone }) {
  const { t, lang, errorText } = useI18n();
  const { user } = useAuth();
  // A new customer's typed fields are kept as a draft on this browser until
  // saved: closing by mistake never loses them.
  const contactTypes = useContactTypes();
  const draftKey = `pme_new_customer_${user.id}`;
  const blank = () => ({
    companyName: '', contactName: '', title: '', emails: '', phones: '', whatsapp: '', website: '',
    country: '', city: '', address: '', lang: lang === 'en' ? 'en' : 'fr', sector: '', contactType: '', source: '', tags: '',
    consentType: 'none', assignedTo: String(user.person?.id || ''),
  });
  const saved = () => { if (editId || initial) return null; try { return JSON.parse(localStorage.getItem(draftKey) || 'null'); } catch { return null; } };
  const [restored, setRestored] = useState(() => !!saved());
  const [f, setF] = useState(() => ({ ...blank(), ...initial, ...(saved() || {}) }));
  useEffect(() => {
    if (editId || initial) return;
    try {
      const typed = Object.entries(f).some(([k, v]) => !['lang', 'consentType', 'assignedTo'].includes(k) && String(v || '').trim());
      if (typed) localStorage.setItem(draftKey, JSON.stringify(f)); else localStorage.removeItem(draftKey);
    } catch { /* private mode */ }
  }, [f]); // eslint-disable-line react-hooks/exhaustive-deps
  const clearDraft = () => { try { localStorage.removeItem(draftKey); } catch { /* private mode */ } };
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const [dup, setDup] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const checkTimer = useRef(null);
  const countries = countryOptions(lang); // the full ISO list: a private contact can be anywhere

  async function checkDup(next) {
    clearTimeout(checkTimer.current);
    checkTimer.current = setTimeout(async () => {
      if (!next.emails.trim() && !next.phones.trim() && !next.companyName.trim()) { setDup(null); return; }
      setChecking(true);
      try { setDup(await api('/workspace/customers/duplicates', { method: 'POST', body: { email: next.emails, phone: next.phones, companyName: next.companyName, excludeId: editId } })); } catch { /* best-effort */ }
      setChecking(false);
    }, 400);
  }
  useEffect(() => { checkDup(f); return () => clearTimeout(checkTimer.current); }, [f.emails, f.phones, f.companyName]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save() {
    setBusy(true); setError('');
    try {
      if (editId) await api(`/workspace/customers/${editId}`, { method: 'PATCH', body: f });
      else await api('/workspace/customers/private', { method: 'POST', body: { ...f, assignedTo: canAssign ? Number(f.assignedTo) || null : undefined } });
      if (!editId) clearDraft();
      onDone();
    } catch (e) { setError(errorText(e)); setBusy(false); }
  }
  const hasDup = dup && (dup.memberMatch || dup.privateMatches?.length > 0);

  return (
    <Modal title={t(editId ? 'ws.priv.editTitle' : 'ws.priv.title')} onClose={onClose} wide>
      {restored && (
        <p className="callout callout-info small ws-draft-note">{t('ws.priv.draftRestored')}{' '}
          <button type="button" className="btn btn-link btn-sm" onClick={() => { clearDraft(); setF({ ...blank(), ...initial }); setRestored(false); }}>{t('ws.priv.draftClear')}</button></p>
      )}
      <div className="ws-form">
        <label className="field"><span className="field-label">{t('ws.priv.companyName')}</span><input value={f.companyName} onChange={set('companyName')} maxLength={190} /></label>
        <label className="field"><span className="field-label">{t('ws.priv.contactName')}</span><input value={f.contactName} onChange={set('contactName')} maxLength={120} /></label>
        <label className="field"><span className="field-label">{t('ws.priv.jobTitle')}</span><input value={f.title} onChange={set('title')} maxLength={120} /></label>
        <label className="field"><span className="field-label">{t('ws.priv.emails')}</span><input value={f.emails} onChange={set('emails')} placeholder="a@exemple.com, b@exemple.com" /></label>
        <label className="field"><span className="field-label">{t('ws.priv.phones')}</span><input value={f.phones} onChange={set('phones')} placeholder="+1 438 555 0100" /></label>
        <label className="field"><span className="field-label">{t('ws.priv.whatsapp')}</span><input value={f.whatsapp} onChange={set('whatsapp')} placeholder="+1 438 555 0100" /></label>
        <label className="field"><span className="field-label">{t('ws.priv.website')}</span><input value={f.website} onChange={set('website')} placeholder="https://" /></label>
        <label className="field"><span className="field-label">{t('ws.priv.lang')}</span><select value={f.lang} onChange={set('lang')}><option value="fr">Français</option><option value="en">English</option></select></label>
        <div className="ws-full"><AddressAutocomplete onPlace={(p) => setF((x) => ({ ...x, address: p.addressLine, city: p.city, country: p.country }))}>
          <input value={f.address} onChange={set('address')} placeholder={t('ws.priv.addressPh')} style={{ marginTop: 6 }} />
        </AddressAutocomplete></div>
        <label className="field"><span className="field-label">{t('ws.cust.country')}</span><select value={f.country} onChange={set('country')}><option value="">—</option>{countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}</select></label>
        <label className="field"><span className="field-label">{t('ws.cust.city')}</span><input value={f.city} onChange={set('city')} maxLength={120} /></label>
        <label className="field"><span className="field-label">{t('ws.priv.sector')}</span><input value={f.sector} onChange={set('sector')} maxLength={190} placeholder={t('ws.priv.sectorPh')} /></label>
        <label className="field"><span className="field-label">{t('ws.priv.type')}</span><select value={f.contactType} onChange={set('contactType')}><option value="">—</option>{contactTypes.map((x) => <option key={x.code} value={x.code}>{lang === 'en' ? x.en : x.fr}</option>)}</select></label>
        <label className="field"><span className="field-label">{t('ws.priv.source')}</span><select value={f.source} onChange={set('source')}><option value="">—</option>{SOURCES.map((x) => <option key={x} value={x}>{t(`ws.priv.sourceOpt.${x}`)}</option>)}</select></label>
        <label className="field"><span className="field-label">{t('ws.cp.tags')}</span><input value={f.tags} onChange={set('tags')} placeholder={t('ws.cp.tagsPh')} /></label>
        <label className="field ws-full"><span className="field-label">{t('ws.priv.consent')}</span>
          <select value={f.consentType} onChange={set('consentType')}>{['none', 'implied', 'express'].map((x) => <option key={x} value={x}>{t(`ws.priv.consentType.${x}`)}</option>)}</select>
          <span className="field-hint">{t('ws.priv.consentHint')}</span>
        </label>
        {!editId && canAssign && <label className="field"><span className="field-label">{t('ws.cust.assignTo')}</span><select value={f.assignedTo} onChange={set('assignedTo')}>{assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></label>}
      </div>
      {checking && <p className="ws-hint">{t('common.loading')}</p>}
      {hasDup && (
        <p className="callout callout-warning small">
          {dup.memberMatch && <>{t('ws.priv.matchMember', { company: dup.memberMatch.company })} </>}
          {dup.privateMatches?.length > 0 && t('ws.priv.matchPrivate', { n: dup.privateMatches.length })}
        </p>
      )}
      <Alert>{error}</Alert>
      <div className="ws-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
        <button type="button" className="btn btn-primary" disabled={busy || (!f.companyName.trim() && !f.contactName.trim())} onClick={save}>{busy ? t('common.saving') : t('common.save')}</button>
      </div>
    </Modal>
  );
}

// Import: file (.csv/.xlsx) → column mapping → preview + duplicate report → commit.
function ImportWizard({ assignees, canAssign, onClose, onDone }) {
  const { t, errorText } = useI18n();
  const { user } = useAuth();
  const [step, setStep] = useState('upload'); // upload | map | report
  const [parsed, setParsed] = useState(null); // { columns, rows, suggested }
  const [mapping, setMapping] = useState({});
  const [to, setTo] = useState(String(user.person?.id || ''));
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [paste, setPaste] = useState('');
  const FIELDS = ['companyName', 'contactName', 'title', 'email', 'phone', 'whatsapp', 'website', 'country', 'city', 'address', 'sector', 'contactType', 'source', 'tags'];

  async function onFile(file) {
    if (!file) return;
    setError('');
    const fd = new FormData(); fd.append('file', file);
    try { const r = await api('/workspace/customers/import/parse', { method: 'POST', form: fd }); setParsed(r); setMapping(r.suggested); setStep('map'); } catch (e) { setError(errorText(e)); }
  }
  async function fromPaste() {
    try { const r = await api('/workspace/customers/import/paste', { method: 'POST', body: { text: paste } }); setParsed(r); setMapping(r.suggested); setStep('map'); } catch (e) { setError(errorText(e)); }
  }
  async function preview() {
    setBusy(true); setError('');
    try { const r = await api('/workspace/customers/import/commit', { method: 'POST', body: { rows: parsed.rows, mapping, dryRun: true } }); setReport(r); setStep('report'); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  }
  async function commit() {
    setBusy(true); setError('');
    try { const r = await api('/workspace/customers/import/commit', { method: 'POST', body: { rows: parsed.rows, mapping, assignedTo: canAssign ? Number(to) || null : undefined } }); onDone(r); } catch (e) { setError(errorText(e)); setBusy(false); }
  }

  return (
    <Modal title={t('ws.import.title')} onClose={onClose} wide>
      {step === 'upload' && (
        <div className="ws-form">
          <label className="field ws-full"><span className="field-label">{t('ws.import.file')}</span>
            <input type="file" accept=".csv,.xlsx,.xlsm" onChange={(e) => onFile(e.target.files?.[0])} /></label>
          <p className="ws-hint ws-full">{t('ws.import.or')}</p>
          <label className="field ws-full"><span className="field-label">{t('ws.import.paste')}</span>
            <textarea rows={6} value={paste} onChange={(e) => setPaste(e.target.value)} placeholder={t('ws.import.pastePh')} /></label>
          <div className="ws-full ws-actions"><button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button><button type="button" className="btn btn-primary" disabled={!paste.trim()} onClick={fromPaste}>{t('ws.import.usePaste')}</button></div>
        </div>
      )}
      {step === 'map' && parsed && (
        <>
          <p className="ws-hint">{t('ws.import.mapHint', { n: parsed.total })}</p>
          <div className="ws-table-wrap" style={{ maxHeight: 280, overflow: 'auto' }}>
            <table className="ws-table">
              <thead><tr>{FIELDS.map((f) => <th key={f}>{t(`ws.import.field.${f}`)}</th>)}</tr></thead>
              <tbody>
                <tr>{FIELDS.map((f) => (
                  <td key={f}><select value={mapping[f] ?? ''} onChange={(e) => setMapping((m) => ({ ...m, [f]: e.target.value === '' ? undefined : Number(e.target.value) }))}>
                    <option value="">—</option>{parsed.columns.map((c, i) => <option key={i} value={i}>{c || `#${i + 1}`}</option>)}
                  </select></td>
                ))}</tr>
                {parsed.rows.slice(0, 5).map((r, i) => (
                  <tr key={i} className="small muted">{FIELDS.map((f) => <td key={f}>{mapping[f] != null ? r[mapping[f]] : ''}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <Alert>{error}</Alert>
          <div className="ws-actions"><button type="button" className="btn btn-ghost" onClick={() => setStep('upload')}>{t('common.back')}</button><button type="button" className="btn btn-primary" disabled={busy} onClick={preview}>{busy ? t('common.loading') : t('ws.import.preview')}</button></div>
        </>
      )}
      {step === 'report' && report && (
        <>
          <ul className="ws-list">
            <li>{t('ws.import.willImport', { n: report.imported })}</li>
            <li>{t('ws.import.willLink', { n: report.matchedMember })}</li>
            <li className="muted">{t('ws.import.dupInFile', { n: report.duplicateInFile })}</li>
            <li className="muted">{t('ws.import.dupExisting', { n: report.duplicateExisting })}</li>
            {report.errors?.length > 0 && <li className="field-error">{t('ws.import.errors', { n: report.errors.length })}</li>}
          </ul>
          {canAssign && <label className="field"><span className="field-label">{t('ws.cust.assignTo')}</span><select value={to} onChange={(e) => setTo(e.target.value)}>{assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></label>}
          <Alert>{error}</Alert>
          <div className="ws-actions"><button type="button" className="btn btn-ghost" onClick={() => setStep('map')}>{t('common.back')}</button><button type="button" className="btn btn-primary" disabled={busy} onClick={commit}>{busy ? t('common.saving') : t('ws.import.commit')}</button></div>
        </>
      )}
    </Modal>
  );
}

// "Envoi groupé" (small campaigns): CASL-aware — consent + unsubscribe
// respected automatically; shows sent/failed/skipped once it starts.
function BulkEmailModal({ ids, onClose, onDone }) {
  const { t, errorText } = useI18n();
  const [templates, setTemplates] = useState([]);
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { api('/workspace/email-templates').then(setTemplates).catch(() => {}); }, []);
  const useTpl = (id) => { const x = templates.find((y) => String(y.id) === id); if (x) { setSubject(x.subject); setHtml(x.html); } };
  async function send() {
    setBusy(true); setError('');
    try { const r = await api('/workspace/customers/bulk-email', { method: 'POST', body: { ids, subject, html } }); onDone(r); } catch (e) { setError(errorText(e)); setBusy(false); }
  }
  return (
    <Modal title={t('ws.bulk.title', { n: ids.length })} onClose={onClose} wide>
      <p className="ws-hint">{t('ws.bulk.caslHint')}</p>
      <div className="ws-form">
        <label className="field"><span className="field-label">{t('ws.mail.template')}</span>
          <select defaultValue="" onChange={(e) => useTpl(e.target.value)}><option value="">—</option>{templates.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.lang.toUpperCase()})</option>)}</select></label>
        <label className="field ws-full"><span className="field-label">{t('ws.mail.subject')}</span><input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={255} /></label>
        <label className="field ws-full"><span className="field-label">{t('ws.mail.bodyPh')}</span><textarea rows={8} value={html} onChange={(e) => setHtml(e.target.value)} /></label>
        <p className="ws-hint ws-full">{t('ws.mail.placeholders')}</p>
      </div>
      <Alert>{error}</Alert>
      <div className="ws-actions"><button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
        <button type="button" className="btn btn-primary" disabled={busy || !subject.trim() || !html.replace(/<[^>]*>/g, '').trim()} onClick={send}>{busy ? t('common.sending') : t('ws.bulk.send')}</button></div>
    </Modal>
  );
}
